import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import SearchField from '../SearchField/SearchField';
import IconButton from '../../atoms/IconButton/IconButton';

import styles from './TableFindBar.module.css';

const FIND_INPUT_ID = 'table-find-input';

/**
 * In-table find bar (Cmd/Ctrl+F).
 * @param {object} props
 * @param {string} props.query
 * @param {(query: string) => void} props.onQueryChange
 * @param {number} props.matchIndex
 * @param {number} props.matchCount
 * @param {() => void} props.onNext
 * @param {() => void} props.onPrev
 * @param {() => void} props.onClose
 * @returns {JSX.Element}
 */
function TableFindBar({
  query,
  onQueryChange,
  matchIndex,
  matchCount,
  onNext,
  onPrev,
  onClose,
}) {
  const { t } = useTranslation();

  useEffect(() => {
    const input = document.getElementById(FIND_INPUT_ID);
    if (!(input instanceof HTMLInputElement)) return;
    input.focus();
    input.select();
  }, []);

  const counter = matchCount === 0
    ? t('table.find.noMatch')
    : t('table.find.matches', { current: matchIndex + 1, total: matchCount });

  return (
    <div
      className={styles['table-find-bar']}
      data-table-find-bar
      role="search"
    >
      <SearchField
        id={FIND_INPUT_ID}
        className={styles['table-find-bar-input']}
        placeholder={t('table.find.placeholder')}
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        onFocus={() => {}}
      />
      <span className={styles['table-find-bar-counter']} aria-live="polite">
        {counter}
      </span>
      <IconButton
        icon="chevron-up-range"
        size="small"
        color="no-background"
        title={t('table.find.prev')}
        onClick={onPrev}
        disabled={matchCount === 0}
      />
      <IconButton
        icon="chevron-down-range"
        size="small"
        color="no-background"
        title={t('table.find.next')}
        onClick={onNext}
        disabled={matchCount === 0}
      />
      <IconButton
        icon="close-md"
        size="small"
        color="no-background"
        title={t('table.find.close')}
        onClick={onClose}
      />
    </div>
  );
}

export default TableFindBar;
